import { useContext } from "react"
import type { LoginRequest, RegisterRequest, UserProfile, UserRole } from "@/shared/api/types"
import { AuthContext } from "./context"

export type NbrAuth = {
  isLoading: boolean
  isAuthenticated: boolean
  account: UserProfile | null
  displayName: string
  roles: UserRole[]
  hasRole: (role: UserRole | UserRole[]) => boolean
  isLibrarian: boolean
  isAdmin: boolean
  login: (returnTo?: string) => void
  authenticate: (credentials: LoginRequest) => Promise<UserProfile>
  register: (details: RegisterRequest) => Promise<UserProfile>
  logout: () => Promise<void>
  error: Error | null
}

/** Public auth facade backed by the API-confirmed current account. */
export const useNbrAuth = (): NbrAuth => {
  const auth = useContext(AuthContext)
  if (!auth) throw new Error("useNbrAuth must be used within AuthProvider")
  const roles = auth.account?.roles ?? []
  const displayName = auth.account?.display_name || auth.account?.username || auth.account?.email || ""

  return {
    isLoading: auth.isLoading,
    isAuthenticated: auth.isAuthenticated,
    account: auth.account,
    displayName,
    roles,
    hasRole: auth.hasRole,
    isLibrarian: roles.includes("librarian") || roles.includes("admin"),
    isAdmin: roles.includes("admin"),
    login: auth.login,
    authenticate: auth.authenticate,
    register: auth.register,
    logout: auth.logout,
    error: auth.error,
  }
}
